import { useState, useEffect, useCallback, type RefObject } from 'react';

/**
 * Hook that tracks pressed/active state for keypad buttons.
 * Keyboard-triggered active state only fires when the focus is inside
 * the same <section> ancestor as the button (i.e. the keypad/dialpad container).
 */
export function usePressActive(options?: { targetKey?: string; buttonRef?: RefObject<HTMLElement | null> }) {
  const { targetKey, buttonRef } = options || {};
  const [isActive, setIsActive] = useState(false);

  const handleMouseDown = useCallback(() => {
    setIsActive(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsActive(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsActive(false);
  }, []);

  useEffect(() => {
    if (!targetKey || !buttonRef?.current) {
      return;
    }

    // Find the closest <section> ancestor — this is the keypad/dialpad container
    const container = buttonRef.current.closest('section');
    if (!container) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }
      // Only react if the focused element is inside the same section
      if (event.key === targetKey && container.contains(document.activeElement)) {
        setIsActive(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === targetKey) {
        setIsActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [targetKey, buttonRef]);

  return {
    isActive,
    pressHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
    },
  };
}
